import { styled } from '../styled';

const VARIANTS = {
  success: {
    bg: 'linear-gradient(135deg, var(--qhs-success, #41C377), #2ea861)',
    color: '#fff',
    shadow: '0 8px 24px rgba(65, 195, 119, 0.32)',
  },
  link: {
    bg: 'linear-gradient(135deg, var(--qhs-brand), var(--qhs-deep))',
    color: '#fff',
    shadow: '0 8px 24px rgba(46, 72, 211, 0.32)',
  },
};

// Circular tile with brand-tinted gradient + drop shadow + pop-in
// animation. Matches the mockup's Tier-3 success affordance — a
// premium, attention-anchoring confirmation surface, not a flat icon.
const Tile = styled('div', { shouldForwardProp: p => p !== '$variant' })(({ $variant }) => ({
  position: 'relative',
  width: 96,
  height: 96,
  borderRadius: '50%',
  background: VARIANTS[$variant].bg,
  color: VARIANTS[$variant].color,
  boxShadow: VARIANTS[$variant].shadow,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  animation: 'qhs-tile-pop 360ms cubic-bezier(0, 0, 0.2, 1) backwards',
  '@keyframes qhs-tile-pop': {
    from: { transform: 'scale(0.6)', opacity: 0 },
    to: { transform: 'scale(1)', opacity: 1 },
  },
  '& > svg.main': { width: 44, height: 44 },
  '&[data-animated="true"] > svg.main polyline': {
    strokeDasharray: 50,
    strokeDashoffset: 50,
    animation: 'qhs-stroke-draw 480ms ease-out 240ms forwards',
  },
  '@keyframes qhs-stroke-draw': {
    to: { strokeDashoffset: 0 },
  },
}));

const BadgeWrap = styled('span')({
  position: 'absolute',
  bottom: -8,
  right: -8,
  width: 36,
  height: 36,
  borderRadius: '50%',
  background: 'var(--qhs-surface)',
  border: '2px solid var(--qhs-surface)',
  color: 'var(--qhs-brand)',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05), 0 4px 12px rgba(0, 0, 0, 0.04)',
  '& svg': { width: 18, height: 18 },
});

const MailIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);

const SmsIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

const DocIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
);

const BADGE_ICONS = { mail: <MailIcon />, sms: <SmsIcon />, doc: <DocIcon /> };

const CheckSvg = () => (
  <svg
    className="main"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const LinkSvg = () => (
  <svg
    className="main"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);

const SuccessTile = ({ variant = 'success', badge = null, animated = true }) => (
  <Tile $variant={variant} data-animated={animated ? 'true' : 'false'}>
    {variant === 'success' ? <CheckSvg /> : <LinkSvg />}
    {badge && BADGE_ICONS[badge] && <BadgeWrap>{BADGE_ICONS[badge]}</BadgeWrap>}
  </Tile>
);

export default SuccessTile;
